/**
 * BaskReport 服务端 URL 常量与拼接。
 * 依据后端 Constants.java 与前端 Url.ts：
 *   BASK_URL=/baskserver, API_PREFIX=/api, FILE_API_ACTION=/file, DATASOURCE_API_ACTION=/datasource
 *   设计器 /baskreport/designer, 预览 /baskreport/view
 * Handler 路由形如 /{handler}/{method}，例如 /api/file/create、/api/datasource/list
 */

export const PATHS = {
  BASK_URL: '/baskserver',
  API_PREFIX: '/api',
  FILE_HANDLER: 'file',
  PACKET_HANDLER: 'packet',
  DATASOURCE_HANDLER: 'datasource',
  REPORT_DESIGNER_URL: '/baskreport/designer',
  REPORT_VIEW_URL: '/baskreport/preview',
} as const;

/** 拼接完整 URL：baseUrl + /baskserver + /api + /{handler}/{method} */
export function fileAction(baseUrl: string, method: string): string {
  return `${baseUrl}${PATHS.BASK_URL}${PATHS.API_PREFIX}/${PATHS.FILE_HANDLER}/${method}`;
}

export function datasourceAction(baseUrl: string, method: string): string {
  return `${baseUrl}${PATHS.BASK_URL}${PATHS.API_PREFIX}/${PATHS.DATASOURCE_HANDLER}/${method}`;
}

/** 拼接完整 URL：baseUrl + /baskserver + /api + /packet/{method}（报表目录树 loadChildren） */
export function packetAction(baseUrl: string, method: string): string {
  return `${baseUrl}${PATHS.BASK_URL}${PATHS.API_PREFIX}/${PATHS.PACKET_HANDLER}/${method}`;
}

/**
 * 数据源库（数据源级元信息）端点：
 *   /baskserver/baskreport/api/datasource-database/{method}
 * 用于列举表/视图、构建字段等（表单参数），比 datasourceAction 更接近设计器行为。
 */
export function databaseAction(baseUrl: string, method: string): string {
  return `${baseUrl}${PATHS.BASK_URL}/baskreport${PATHS.API_PREFIX}/${PATHS.DATASOURCE_HANDLER}-database/${method}`;
}

/**
 * 服务端数据源定义端点：
 *   /baskserver/baskreport/api/server-datasource/{method}
 * 用于加载已配置服务器数据源的完整定义（如 loadDatasourceDefs）。
 */
export function serverDatasourceAction(baseUrl: string, method: string): string {
  return `${baseUrl}${PATHS.BASK_URL}/baskreport${PATHS.API_PREFIX}/server-datasource/${method}`;
}

/**
 * BeanDataset 端点（无数据源的数据集，由后端 bean 提供数据）：
 *   /baskserver/baskreport/api/datasource-bean/{method}
 * 用于加载 BeanDataset 数据集列表（如 loadDatasets）。
 */
export function beanDatasourceAction(baseUrl: string, method: string): string {
  return `${baseUrl}${PATHS.BASK_URL}/baskreport${PATHS.API_PREFIX}/datasource-bean/${method}`;
}

/**
 * 设计器端点（表达式函数库等设计器侧元信息）：
 *   /baskserver/baskreport/api/designer/{method}
 * 用于加载设计器可用函数（如 loadFunctions，生成表达式前参考）。
 */
export function designerAction(baseUrl: string, method: string): string {
  return `${baseUrl}${PATHS.BASK_URL}/baskreport${PATHS.API_PREFIX}/designer/${method}`;
}

export function designerUrl(
  baseUrl: string,
  fileId: number | string,
  tenantId?: string,
): string {
  const tenant = tenantId ? `&tenantId=${tenantId}` : '';
  return `${baseUrl}${PATHS.BASK_URL}${PATHS.REPORT_DESIGNER_URL}?file=${fileId}${tenant}`;
}

export function viewUrl(
  baseUrl: string,
  fileId: number | string,
  tenantId?: string,
): string {
  const tenant = tenantId ? `&tenantId=${tenantId}` : '';
  return `${baseUrl}${PATHS.BASK_URL}${PATHS.REPORT_VIEW_URL}?file=${fileId}${tenant}`;
}
